import { Component, type ErrorInfo, type ReactNode } from "react";
import { normalizeLanguageCode, pickCopyRecord } from "../lib/i18n";
import { NorvoAttribution } from "./brand/NorvoAttribution";

type ErrorCopy = { title: string; body: string; reload: string; tryAgain: string; hint: string };

const COPY: Record<string, ErrorCopy> = {
  DA: {
    title: "Noget gik galt",
    body: "En del af appen kunne ikke vises. Dine bogdata er som regel gemt lokalt og på din konto.",
    reload: "Genindlæs siden",
    tryAgain: "Prøv igen",
    hint: "Hvis problemet fortsætter: tryk Ctrl+Shift+R (eller ryd cache) og åbn bogen igen fra oversigten.",
  },
  EN: {
    title: "Something went wrong",
    body: "Part of the app could not be displayed. Your book data is usually saved locally and on your account.",
    reload: "Reload page",
    tryAgain: "Try again",
    hint: "If it keeps happening: hard-refresh (Ctrl+Shift+R) and open your book again from the dashboard.",
  },
  SE: {
    title: "Något gick fel",
    body: "En del av appen kunde inte visas. Dina bokdata är vanligtvis sparade lokalt och på ditt konto.",
    reload: "Ladda om sidan",
    tryAgain: "Försök igen",
    hint: "Om det fortsätter: hårduppdatera (Ctrl+Shift+R), rensa webbplatsdata och öppna boken igen från översikten.",
  },
  DE: {
    title: "Etwas ist schiefgelaufen",
    body: "Ein Teil der App konnte nicht angezeigt werden. Deine Buchdaten sind in der Regel lokal und in deinem Konto gespeichert.",
    reload: "Seite neu laden",
    tryAgain: "Erneut versuchen",
    hint: "Wenn es weiter passiert: Hard-Refresh (Strg+Umschalt+R) und das Buch erneut aus der Übersicht öffnen.",
  },
  FR: {
    title: "Une erreur s'est produite",
    body: "Une partie de l'application n'a pas pu s'afficher. Vos données de livre sont en général enregistrées localement et sur votre compte.",
    reload: "Recharger la page",
    tryAgain: "Réessayer",
    hint: "Si cela continue : actualisation forcée (Ctrl+Maj+R) puis rouvrez le livre depuis l'aperçu.",
  },
  IT: {
    title: "Qualcosa è andato storto",
    body: "Una parte dell'app non può essere mostrata. I dati del libro sono di solito salvati in locale e sul tuo account.",
    reload: "Ricarica pagina",
    tryAgain: "Riprova",
    hint: "Se continua: aggiornamento forzato (Ctrl+Maiusc+R) e riapri il libro dalla panoramica.",
  },
  NL: {
    title: "Er ging iets mis",
    body: "Een deel van de app kon niet worden weergegeven. Je boekgegevens staan meestal lokaal en op je account opgeslagen.",
    reload: "Pagina herladen",
    tryAgain: "Opnieuw proberen",
    hint: "Als het blijft gebeuren: harde verversing (Ctrl+Shift+R) en open je boek opnieuw vanuit het overzicht.",
  },
  ES: {
    title: "Algo salió mal",
    body: "No se pudo mostrar parte de la aplicación. Los datos de tu libro suelen guardarse localmente y en tu cuenta.",
    reload: "Recargar página",
    tryAgain: "Intentar de nuevo",
    hint: "Si sigue ocurriendo: actualización forzada (Ctrl+Mayús+R) y abre el libro de nuevo desde el panel.",
  },
  PT: {
    title: "Algo correu mal",
    body: "Parte da aplicação não pôde ser mostrada. Os dados do livro costumam estar guardados localmente e na sua conta.",
    reload: "Recarregar página",
    tryAgain: "Tentar novamente",
    hint: "Se continuar: atualização forçada (Ctrl+Shift+R) e abra o livro novamente a partir da visão geral.",
  },
  PL: {
    title: "Coś poszło nie tak",
    body: "Część aplikacji nie mogła zostać wyświetlona. Dane książki są zwykle zapisane lokalnie i na koncie.",
    reload: "Odśwież stronę",
    tryAgain: "Spróbuj ponownie",
    hint: "Jeśli problem wraca: twarde odświeżenie (Ctrl+Shift+R) i otwórz książkę ponownie z przeglądu.",
  },
};

function readLang(): string {
  try {
    return localStorage.getItem("bbg-language") || "EN";
  } catch {
    return "EN";
  }
}

function errorCopyForLang(lang: string): ErrorCopy {
  return pickCopyRecord(COPY, normalizeLanguageCode(lang));
}

type Props = { children: ReactNode };
type State = { hasError: boolean; error: Error | null };

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[BookBuilderGo]", error, info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleTryAgain = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const copy = errorCopyForLang(readLang());
    const showDetails = import.meta.env.DEV && this.state.error;

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "#F4F6FB",
          fontFamily: "'DM Sans', -apple-system, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 440,
            width: "100%",
            background: "#fff",
            border: "1px solid rgba(200, 194, 240, 0.2)",
            borderRadius: 22,
            padding: "28px 24px",
            boxShadow: "0 18px 44px rgba(26, 26, 62, 0.08)",
          }}
        >
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1E2A3A", marginBottom: 10 }}>
            {copy.title}
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: "#3D4F66", marginBottom: 12 }}>
            {copy.body}
          </p>
          {showDetails ? (
            <pre
              style={{
                fontSize: 11,
                lineHeight: 1.45,
                color: "#C95C74",
                background: "#FAE8EC",
                padding: 12,
                borderRadius: 12,
                overflow: "auto",
                maxHeight: 120,
                marginBottom: 12,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {this.state.error?.message}
            </pre>
          ) : null}
          <p style={{ fontSize: 13, lineHeight: 1.55, color: "#7A8FA8", marginBottom: 20 }}>
            {copy.hint}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button
              type="button"
              onClick={this.handleTryAgain}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 999,
                border: "1.5px solid #D2D8E8",
                background: "#fff",
                color: "#1E2A3A",
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {copy.tryAgain}
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 999,
                border: "none",
                background: "#7C6FCD",
                color: "#fff",
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {copy.reload}
            </button>
          </div>
          <p style={{ marginTop: 16, textAlign: "center" }}>
            <NorvoAttribution lang={readLang()} variant="inline" />
          </p>
        </div>
      </div>
    );
  }
}
